import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ONBOARDING_FIRST_FEATURES } from "@/lib/features";

/**
 * Mandatory 3-step onboarding survey for beta testers.
 * No Skip / Maybe Later / X — the only way past this screen is Submit.
 *
 * Answers are held in local state per step, so refreshing loses them (the
 * server never sees partial state until Submit). We accept that trade-off in
 * exchange for a single atomic "flip the gate" transaction on the backend.
 */
const ROLES = [
  { id: "solo_bcba",         label: "Solo BCBA" },
  { id: "clinic_bcba",       label: "BCBA working in a clinic" },
  { id: "clinic_owner",      label: "Clinic owner" },
  { id: "clinical_director", label: "Clinical director" },
  { id: "other_aba",         label: "Other ABA professional" },
];

const METHODS = [
  { id: "canva",             label: "Canva" },
  { id: "powerpoint",        label: "PowerPoint" },
  { id: "google_slides",     label: "Google Slides" },
  { id: "google_images",     label: "Google Images / web searches" },
  { id: "premade",           label: "Premade materials" },
  { id: "another_platform",  label: "Another ABA platform" },
  { id: "manual",            label: "Create them manually" },
  { id: "other",             label: "Other" },
];

const TIMES = [
  { id: "lt_1h",   label: "Less than 1 hour" },
  { id: "1_to_2h", label: "1–2 hours" },
  { id: "3_to_5h", label: "3–5 hours" },
  { id: "gt_5h",   label: "More than 5 hours" },
];

export default function BetaOnboardingFlow() {
  const { refresh, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState("");
  const [roleOther, setRoleOther] = useState("");
  const [methods, setMethods] = useState([]);
  const [methodsOther, setMethodsOther] = useState("");
  const [time, setTime] = useState("");
  const [goal, setGoal] = useState("");
  const [firstFeature, setFirstFeature] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleMethod = (id) => {
    setMethods((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const step1Ok = Boolean(role) && (role !== "other_aba" || roleOther.trim().length > 0);
  const step2Ok = methods.length > 0 && (!methods.includes("other") || methodsOther.trim().length > 0) && Boolean(time);
  const step3Ok = goal.trim().length >= 3 && Boolean(firstFeature);

  const submit = async () => {
    if (!step1Ok || !step2Ok || !step3Ok) return;
    setBusy(true);
    try {
      await http.post("/beta/onboarding", {
        role, role_other: roleOther.trim(),
        materials_methods: methods, materials_methods_other: methodsOther.trim(),
        weekly_prep_time: time, primary_goal: goal.trim(), first_feature: firstFeature,
      });
      await refresh();  // pulls beta_onboarding_completed_at → gate unmounts
      toast.success("Welcome to Discrimina.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="beta-onboarding-flow">
      {/* Minimal top bar — no sidebar / no navigation, only a Sign out escape hatch */}
      <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 justify-between">
        <div className="flex items-center gap-2">
          <img src="/icon-192.png" className="w-7 h-7 rounded" alt="Discrimina" />
          <div className="font-display font-extrabold text-slate-900">Discrimina</div>
        </div>
        <button onClick={logout} className="text-xs text-slate-500 hover:text-slate-900" data-testid="beta-onboarding-logout">
          Sign out
        </button>
      </header>

      <main className="flex-1 flex items-start sm:items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
          {/* Progress */}
          <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-teal-700" data-testid={`beta-onboarding-step-${step}`}>
            Step {step} of 3
          </div>
          <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-teal-600 transition-all" style={{ width: `${(step / 3) * 100}%` }} />
          </div>

          {step === 1 && (
            <div className="mt-6">
              <h2 className="font-display text-xl font-extrabold">Tell us about you</h2>
              <p className="text-xs text-slate-500 mt-1">Which best describes you?</p>
              <div className="grid gap-2 mt-4">
                {ROLES.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setRole(r.id)}
                    data-testid={`onboarding-role-${r.id}`}
                    className={`text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                      role === r.id ? "bg-teal-50 border-teal-500 text-teal-900 font-semibold" : "bg-white border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              {role === "other_aba" && (
                <input
                  type="text"
                  value={roleOther}
                  onChange={(e) => setRoleOther(e.target.value)}
                  placeholder="Please specify your role"
                  data-testid="onboarding-role-other-input"
                  className="mt-3 w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              )}
            </div>
          )}

          {step === 2 && (
            <div className="mt-6">
              <h2 className="font-display text-xl font-extrabold">How are you creating materials now?</h2>
              <p className="text-xs text-slate-500 mt-1">Select all that apply.</p>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {METHODS.map((m) => {
                  const active = methods.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      onClick={() => toggleMethod(m.id)}
                      data-testid={`onboarding-method-${m.id}`}
                      className={`text-left px-3 py-2 rounded-xl border text-sm transition-colors ${
                        active ? "bg-teal-50 border-teal-500 text-teal-900 font-semibold" : "bg-white border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {active && <Check className="w-3 h-3 inline mr-1" />} {m.label}
                    </button>
                  );
                })}
              </div>
              {methods.includes("other") && (
                <input
                  type="text"
                  value={methodsOther}
                  onChange={(e) => setMethodsOther(e.target.value)}
                  placeholder="Please tell us more"
                  data-testid="onboarding-methods-other-input"
                  className="mt-3 w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              )}
              <div className="mt-6">
                <p className="text-xs text-slate-500">Approximately how much time per week do you spend creating or finding visual teaching materials?</p>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {TIMES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTime(t.id)}
                      data-testid={`onboarding-time-${t.id}`}
                      className={`text-left px-3 py-2 rounded-xl border text-sm transition-colors ${
                        time === t.id ? "bg-teal-50 border-teal-500 text-teal-900 font-semibold" : "bg-white border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="mt-6">
              <h2 className="font-display text-xl font-extrabold">What do you want Discrimina to make easier?</h2>
              <Textarea
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="What are you hoping we help you with?"
                data-testid="onboarding-goal-input"
                className="mt-3 rounded-xl resize-none"
              />
              <div className="mt-6">
                <p className="text-xs text-slate-500 mb-2">What feature are you most interested in trying first?</p>
                <div className="grid gap-2">
                  {ONBOARDING_FIRST_FEATURES.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setFirstFeature(f.id)}
                      data-testid={`onboarding-firstfeature-${f.id}`}
                      className={`text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                        firstFeature === f.id ? "bg-teal-50 border-teal-500 text-teal-900 font-semibold" : "bg-white border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            {step > 1 ? (
              <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={busy} data-testid="onboarding-back-btn" className="rounded-full">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : <div />}
            {step < 3 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={(step === 1 && !step1Ok) || (step === 2 && !step2Ok)}
                data-testid="onboarding-continue-btn"
                className="rounded-full"
              >
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={!step3Ok || busy} data-testid="onboarding-submit-btn" className="rounded-full">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <>Start Using Discrimina</>}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
